import { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Plus, Minus } from '@phosphor-icons/react';
import { applySearchedLabel, removeSearchedLabel, styleUsesSearchedLabel } from './searchedLabel';

export const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiaGFpcnljb3ciLCJhIjoiY2xyZzBtOHJsMGM5bDJscm04MXZ4YWE4NiJ9.7IOy_XqXBvetjSQx1WXxeQ';
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

export type MapStyleKey = 'default' | 'suburbs';

export const MAP_STYLES: Record<MapStyleKey, { label: string; path: string; url: string }> = {
  default: {
    label: 'Streets',
    path: 'hairycow/cl1kdnmdi003314o6yl04r1hd',
    url: 'mapbox://styles/hairycow/cl1kdnmdi003314o6yl04r1hd',
  },
  suburbs: {
    label: 'Suburbs',
    path: 'hairycow/cmsi19bj9000801skb21udzmx',
    url: 'mapbox://styles/hairycow/cmsi19bj9000801skb21udzmx',
  },
};

// Default starting zoom for the live map (and therefore the preview and
// the printed map, both derived from wherever the customer leaves it).
// A desktop viewport is far wider than the square print, so at a given
// zoom it shows a much larger slice of map than what actually gets
// printed — start desktop two levels tighter so the default framing is
// closer to the real thing. Mobile keeps 13. Called lazily (at map
// init / first render) rather than at module load so the viewport is
// reliably measured; the `md:` breakpoint (768px) is what the rest of
// the app treats as "desktop".
export function getDefaultZoom(): number {
  if (typeof window === 'undefined') return 13;
  const viewport = Math.max(window.innerWidth || 0, document.documentElement?.clientWidth || 0);
  // Fall back to the physical screen width if the viewport can't be
  // measured at the moment this runs, so a transient 0 never forces the
  // mobile default onto a desktop.
  const width = viewport || window.screen?.width || 0;
  return width >= 768 ? 15 : 13;
}

// Builds a Mapbox Static Images API URL matching the live map's style,
// so emails/previews show the same look as the app itself.
export function getStaticMapPreviewUrl(coords: [number, number], zoom: number, styleKey: MapStyleKey = 'default', width = 600, height = 400): string {
  const [lng, lat] = coords;
  const stylePath = MAP_STYLES[styleKey].path;
  return `https://api.mapbox.com/styles/v1/${stylePath}/static/${lng},${lat},${zoom},0/${width}x${height}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`;
}

function createCustomMarker() {
  const el = document.createElement('div');
  el.style.width = '24px';
  el.style.height = '24px';
  el.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" fill="white" stroke="#404342" stroke-width="2" />
    </svg>
  `;
  return el;
}

export interface MapLocation {
  coords: [number, number];
  zoom: number;
  // Reverse-geocoded name of wherever the map is currently centred — updated
  // on every pan/zoom, so it can drift to a road/address name. Used for the
  // order's location text and the Streets-style badge.
  name: string;
  address: string;
  // The place that was actually searched / the random landing location.
  // Set only on search, suggestion pick, or initial load — never by panning.
  // This is what the big searched-location label shows, on the live map and
  // in every rendered preview, so they always agree.
  searchedName: string;
}

interface MapboxMapProps {
  onLocationSelect: (locationName: string) => void;
  searchQuery: string;
  selectedLocation?: { coords: [number, number]; name: string; address: string; token: number } | null;
  initialLocation: { coords: [number, number]; name: string; address: string };
  mapStyleKey: MapStyleKey;
  className?: string;
  locationRef?: React.MutableRefObject<MapLocation>;
}

export function MapboxMap({ onLocationSelect, searchQuery, selectedLocation, initialLocation, mapStyleKey, className = '', locationRef }: MapboxMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);

  // The place the map is "about" — the random landing location, or whatever
  // was last searched / picked. Drives the big searched-location label on the
  // Suburbs style. Pinned to this point; it does not chase manual panning.
  const currentPlace = useRef<{ name: string; coords: [number, number] }>({
    name: initialLocation.name,
    coords: initialLocation.coords,
  });
  const mapStyleKeyRef = useRef(mapStyleKey);
  useEffect(() => {
    mapStyleKeyRef.current = mapStyleKey;
  }, [mapStyleKey]);

  // Apply (Suburbs) or clear (Streets) the searched-location label for
  // wherever currentPlace points right now. Safe to call any time; no-ops
  // until the style is loaded. Called on first load, after every
  // search/suggestion, and after a style switch (setStyle wipes custom
  // layers, so it has to be re-applied).
  const syncSearchedLabel = useCallback(() => {
    const m = map.current;
    if (!m || !m.isStyleLoaded()) return;
    if (styleUsesSearchedLabel(mapStyleKeyRef.current)) {
      applySearchedLabel(m, currentPlace.current);
    } else {
      removeSearchedLabel(m);
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAP_STYLES[mapStyleKey].url,
      center: initialLocation.coords,
      zoom: getDefaultZoom(),
    });

    // Add initial marker with custom design
    marker.current = new mapboxgl.Marker({ element: createCustomMarker() })
      .setLngLat(initialLocation.coords)
      .addTo(map.current);

    // Draw the searched-location label once the initial style is ready.
    map.current.on('load', syncSearchedLabel);

    // Ensure map resizes to fill container
    setTimeout(() => {
      map.current?.resize();
    }, 100);

    // Keep locationRef in sync with any manual pan/zoom. Coordinates update
    // immediately; the address is refreshed via reverse geocoding (debounced)
    // so it never goes stale relative to where the pin actually ends up —
    // this matters because the "Order" email's address needs to match the
    // coordinates exactly (e.g. to be searchable in Mapbox Studio).
    let isFirstMoveEnd = true;
    let reverseGeocodeTimer: ReturnType<typeof setTimeout> | undefined;

    const onMoveEnd = () => {
      if (!map.current || !locationRef) return;
      const { lng, lat } = map.current.getCenter();
      locationRef.current.coords = [lng, lat];
      locationRef.current.zoom = map.current.getZoom();

      // Skip the programmatic move that happens when the map first loads —
      // initialLocation's address is already correct, no need to re-fetch it.
      if (isFirstMoveEnd) {
        isFirstMoveEnd = false;
        return;
      }

      if (reverseGeocodeTimer) clearTimeout(reverseGeocodeTimer);
      reverseGeocodeTimer = setTimeout(async () => {
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`
          );
          const data = await response.json();
          const features: Array<{ text?: string; place_name?: string; place_type?: string[] }> = data.features ?? [];
          const addressFeature = features[0];
          if (!addressFeature || !locationRef) return;

          // Prefer a town/suburb-level result for the display name so a
          // zoomed-out or rural centre reads "Nagambie", not "High Street".
          // The address still comes from the most specific (first) result.
          const placeFeature =
            features.find((f) =>
              (f.place_type ?? []).some((t) => t === 'place' || t === 'locality' || t === 'neighborhood')
            ) ?? addressFeature;
          const placeName = placeFeature.text || placeFeature.place_name;
          const fullAddress = addressFeature.place_name || placeName;

          // Only apply if the map hasn't moved again since this lookup started.
          const current = map.current?.getCenter();
          if (current && Math.abs(current.lng - lng) < 0.0001 && Math.abs(current.lat - lat) < 0.0001) {
            locationRef.current.name = placeName;
            locationRef.current.address = fullAddress;
            onLocationSelect(placeName);
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
        }
      }, 400);
    };
    map.current.on('moveend', onMoveEnd);

    return () => {
      map.current?.remove();
      if (reverseGeocodeTimer) clearTimeout(reverseGeocodeTimer);
    };
  }, []);

  // Switch the live map's style when the user picks a different one from the
  // dropdown. Skips the very first render since the constructor above
  // already set the correct initial style — calling setStyle again
  // immediately after creation is unnecessary and can cause a flash.
  const isFirstStyleRender = useRef(true);
  useEffect(() => {
    if (isFirstStyleRender.current) {
      isFirstStyleRender.current = false;
      return;
    }
    if (!map.current) return;
    map.current.setStyle(MAP_STYLES[mapStyleKey].url);
    // setStyle drops all custom layers and resets the native label filters —
    // re-apply the searched-location label once the new style has loaded.
    map.current.once('style.load', syncSearchedLabel);
  }, [mapStyleKey, syncSearchedLabel]);

  // Suburbs style intentionally has no marker pin — the style's own place
  // labels already identify the location, so the pin is redundant clutter
  // for this style specifically. Toggling display rather than
  // adding/removing the marker keeps its position in sync for free.
  useEffect(() => {
    if (!marker.current) return;
    marker.current.getElement().style.display = mapStyleKey === 'suburbs' ? 'none' : '';
  }, [mapStyleKey]);

  // Handle search
  useEffect(() => {
    if (!searchQuery || !map.current) return;

    const searchLocation = async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${mapboxgl.accessToken}`
        );
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          const [lng, lat] = feature.center;
          const placeName = feature.text || feature.place_name;
          const fullAddress = feature.place_name || placeName;

          // Update map position
          map.current?.flyTo({
            center: [lng, lat],
            zoom: getDefaultZoom(),
            essential: true,
          });

          // Update marker
          if (marker.current) {
            marker.current.setLngLat([lng, lat]);
          } else if (map.current) {
            marker.current = new mapboxgl.Marker({ element: createCustomMarker() })
              .setLngLat([lng, lat])
              .addTo(map.current);
          }

          // Update ref so parent can read coords on demand (e.g. Order click)
          if (locationRef) locationRef.current = { coords: [lng, lat], zoom: getDefaultZoom(), name: placeName, address: fullAddress, searchedName: placeName };
          currentPlace.current = { name: placeName, coords: [lng, lat] };
          syncSearchedLabel();
          // Notify parent component
          onLocationSelect(placeName);
        }
      } catch (error) {
        console.error('Error searching location:', error);
      }
    };

    searchLocation();
  }, [searchQuery, onLocationSelect, syncSearchedLabel]);

  // Handle a specific suggestion pick — use its exact coordinates directly,
  // never re-geocode by name (which can resolve to a different match than
  // the one the user actually clicked, especially for ambiguous place names).
  useEffect(() => {
    if (!selectedLocation || !map.current) return;

    const [lng, lat] = selectedLocation.coords;

    map.current.flyTo({
      center: [lng, lat],
      zoom: getDefaultZoom(),
      essential: true,
    });

    if (marker.current) {
      marker.current.setLngLat([lng, lat]);
    } else {
      marker.current = new mapboxgl.Marker({ element: createCustomMarker() })
        .setLngLat([lng, lat])
        .addTo(map.current);
    }

    if (locationRef) locationRef.current = { coords: [lng, lat], zoom: getDefaultZoom(), name: selectedLocation.name, address: selectedLocation.address, searchedName: selectedLocation.name };
    currentPlace.current = { name: selectedLocation.name, coords: [lng, lat] };
    syncSearchedLabel();
    onLocationSelect(selectedLocation.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, onLocationSelect]);

  const handleZoomIn = () => map.current?.zoomIn();
  const handleZoomOut = () => map.current?.zoomOut();

  return (
    <>
      <div ref={mapContainer} className={`absolute inset-0 size-full ${className}`} />

      {/* Custom zoom controls (Phosphor Plus/Minus) instead of Mapbox's
          default NavigationControl, so the icon weight/style matches the
          rest of the app's icon set. Positioned to align with the search
          bar's edges (16px inset on mobile, 40px on desktop) with the same
          16px gap below it used elsewhere. */}
      <div className="absolute top-[80px] right-[16px] md:top-[104px] md:right-[40px] z-10 flex flex-col gap-[12px]">
        <button
          type="button"
          onClick={handleZoomIn}
          aria-label="Zoom in"
          className="size-[48px] rounded-full border-2 border-[#404342] bg-white hover:bg-[#f9fafb] flex items-center justify-center transition-colors cursor-pointer shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)]"
        >
          <Plus size={22} weight="regular" className="text-[#404342]" />
        </button>
        <button
          type="button"
          onClick={handleZoomOut}
          aria-label="Zoom out"
          className="size-[48px] rounded-full border-2 border-[#404342] bg-white hover:bg-[#f9fafb] flex items-center justify-center transition-colors cursor-pointer shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)]"
        >
          <Minus size={22} weight="regular" className="text-[#404342]" />
        </button>
      </div>
    </>
  );
}
